import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
import ProtectedRoute from "./components/ProtectedRoute";
import FieldProtectedRoute from "./components/FieldProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import "./App.css";
import InstallPrompt from "./components/InstallPrompt";

const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const FieldLogin = lazy(() => import("./pages/FieldLogin"));
const FieldDashboard = lazy(() => import("./pages/FieldDashboard"));
const FieldOnboarding = lazy(() => import("./pages/FieldOnboarding"));
const StaticPage = lazy(() => import("./pages/StaticPage"));
const QRActivationPage = lazy(() => import("./pages/QRActivationPage"));
const TrendingPage = lazy(() => import("./pages/TrendingPage"));
const LocationsPage = lazy(() => import("./pages/LocationsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/r/:slug" element={<ReviewPage />} />
      <Route path="/s/:slug" element={<ShopPage />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requireAdmin>
            <AdminPanel />
          </ProtectedRoute>
        } 
      />
      
      {/* Field Boy Routes - OTP Auth */}
      <Route path="/field" element={<FieldLogin />} />
      <Route path="/field/login" element={<FieldLogin />} />
      <Route 
        path="/field/dashboard" 
        element={
          <FieldProtectedRoute>
            <FieldDashboard />
          </FieldProtectedRoute>
        } 
      />
      <Route 
        path="/field/onboard" 
        element={
          <FieldProtectedRoute>
            <FieldOnboarding />
          </FieldProtectedRoute>
        } 
      />
      <Route 
        path="/field/activate/:qrCode" 
        element={
          <FieldProtectedRoute>
            <FieldOnboarding activationMode />
          </FieldProtectedRoute>
        } 
      />
      
      {/* Static Pages & QR Activation */}
      <Route path="/page/:slug" element={<StaticPage />} />
      <Route path="/activate/:qrCode" element={<QRActivationPage />} />
      
      {/* Trending Businesses - dynamic permalink */}
      <Route path="/:category/top-10-business-in-:city" element={<TrendingPage />} />

      {/* Multi-location listing page */}
      <Route path="/l/:slug" element={<LocationsPage />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <InstallPrompt />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
